import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api, getRequests, respondRequest } from "@/lib/api";
import { toast } from "sonner";
import {
  Send, MessageCircle, Heart, Users, Shield, Loader2, Phone,
  Check, X, UserPlus, MapPin, GraduationCap, Briefcase, Baby, ChevronLeft, ChevronRight
} from "lucide-react";

export default function WhatsApp() {
  const { user, refresh } = useAuth();
  const [sentRequests, setSentRequests] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [profilesLoading, setProfilesLoading] = useState({});
  const [galleryIndexes, setGalleryIndexes] = useState({});
  const [loadingSent, setLoadingSent] = useState(true);
  const [loadingIncoming, setLoadingIncoming] = useState(true);
  const [activeTab, setActiveTab] = useState("sent");
  const [respondingId, setRespondingId] = useState(null);

  const loadSentRequests = async () => {
    try {
      const res = await api.get("/whatsapp/contacts");
      const pending = res.data?.pending_requests || [];
      setSentRequests(pending);
      pending.forEach((req) => {
        if (!profiles[req.user_id]) {
          loadProfileForUser(req.user_id);
        }
      });
    } catch (err) {
      toast.error("Failed to load sent requests");
    } finally {
      setLoadingSent(false);
    }
  };

  const loadIncomingRequests = async () => {
    try {
      const data = await getRequests();
      setIncomingRequests(data || []);
      data.forEach((req) => {
        if (!profiles[req.from_user_id]) {
          loadProfileForUser(req.from_user_id);
        }
      });
    } catch (err) {
      toast.error("Failed to load requests");
    } finally {
      setLoadingIncoming(false);
    }
  };

  const loadProfileForUser = async (userId) => {
    setProfilesLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await api.get(`/users/${userId}/profile`);
      setProfiles(prev => ({ ...prev, [userId]: res.data }));
    } catch (err) {
      console.error(`Failed to load profile for ${userId}`, err);
    } finally {
      setProfilesLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  useEffect(() => {
    loadSentRequests();
    loadIncomingRequests();
  }, []);

  const handleRespond = async (requestId, action) => {
    setRespondingId(requestId);
    try {
      await respondRequest(requestId, action);
      toast.success(action === "accept" ? "Request accepted!" : "Request rejected");
      await Promise.all([loadSentRequests(), loadIncomingRequests()]);
      await refresh();
    } catch (err) {
      toast.error("Failed to respond");
    } finally {
      setRespondingId(null);
    }
  };

  const openWhatsApp = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/[^\d+]/g, "");
    const url = `https://wa.me/${cleaned}?text=${encodeURIComponent(
      "Hi! I saw your profile on Haven and would love to connect."
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // P3 FIX: Dialog for when user has no phone number
  const showNoPhoneDialog = (displayName) => {
    const name = displayName || "This user";
    // Using confirm() as a simple dialog — replace with toast or custom modal if desired
    toast.info(
      `${name} hasn't shared their WhatsApp number with you yet.`,
      { duration: 5000 }
    );
  };

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const getGalleryImages = (profile) =>
    [profile.profile_image, ...(profile.gallery_images || [])].filter(Boolean);

  const handlePrevImage = (e, userId) => {
    e.stopPropagation();
    const profile = profiles[userId];
    if (!profile) return;
    const images = getGalleryImages(profile);
    setGalleryIndexes(prev => ({
      ...prev,
      [userId]: (prev[userId] || 0) === 0 ? images.length - 1 : (prev[userId] || 0) - 1,
    }));
  };

  const handleNextImage = (e, userId) => {
    e.stopPropagation();
    const profile = profiles[userId];
    if (!profile) return;
    const images = getGalleryImages(profile);
    setGalleryIndexes(prev => ({
      ...prev,
      [userId]: (prev[userId] || 0) === images.length - 1 ? 0 : (prev[userId] || 0) + 1,
    }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold">Requests</h1>
            <p className="text-sm text-[#6B6B70]">Manage your connection requests</p>
          </div>
        </div>

        <div className="flex gap-2 mb-5 sm:mb-6 border-b border-[#E7E5E0] pb-3 overflow-x-auto">
          <button
            onClick={() => setActiveTab("sent")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "sent" ? "bg-purple-100 text-purple-700" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Send className="w-4 h-4" />
            Sent Request ({sentRequests.length})
          </button>
          <button
            onClick={() => setActiveTab("incoming")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "incoming" ? "bg-purple-100 text-purple-700" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <UserPlus className="w-4 h-4" />
            Accept Request ({incomingRequests.length})
          </button>
        </div>

        {activeTab === "sent" && (
          <div>
            {loadingSent ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : sentRequests.length === 0 ? (
              <div className="text-center py-16">
                <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No pending requests sent yet.</p>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {sentRequests.map((req) => {
                  const profile = profiles[req.user_id];
                  const isLoadingProfile = profilesLoading[req.user_id];

                  return (
                    <div key={req.request_id} className="bg-white border border-[#E7E5E0] rounded-2xl overflow-hidden shadow-sm">
                      {isLoadingProfile || !profile ? (
                        <div className="p-4 animate-pulse">
                          <div className="flex gap-4">
                            <div className="w-24 h-24 bg-gray-200 rounded-xl" />
                            <div className="flex-1 space-y-3">
                              <div className="h-4 bg-gray-200 rounded w-3/4" />
                              <div className="h-3 bg-gray-200 rounded w-1/2" />
                              <div className="h-3 bg-gray-200 rounded w-full" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col md:flex-row">
                          <div className="relative w-full md:w-2/5 h-72 sm:h-80 md:h-auto bg-gray-200">
                            {(() => {
                              const images = getGalleryImages(profile);
                              const currentIdx = galleryIndexes[req.user_id] || 0;
                              if (images.length === 0) {
                                return (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <Users className="w-16 h-16 text-gray-400" />
                                  </div>
                                );
                              }
                              return (
                                <>
                                  <img
                                    src={images[currentIdx]}
                                    alt={profile.display_name}
                                    className="absolute inset-0 w-full h-full object-cover"
                                  />
                                  {images.length > 1 && (
                                    <>
                                      <button
                                        onClick={(e) => handlePrevImage(e, req.user_id)}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
                                      >
                                        <ChevronLeft className="w-5 h-5" />
                                      </button>
                                      <button
                                        onClick={(e) => handleNextImage(e, req.user_id)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
                                      >
                                        <ChevronRight className="w-5 h-5" />
                                      </button>
                                      <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1">
                                        {images.map((_, i) => (
                                          <span
                                            key={i}
                                            className={`h-1.5 rounded-full ${
                                              i === currentIdx ? "w-4 bg-white" : "w-1.5 bg-white/50"
                                            }`}
                                          />
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </div>

                          <div className="flex-1 p-4 sm:p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="font-display text-xl font-semibold flex items-center gap-2">
                                  {profile.display_name || "Anonymous"}
                                  {profile.verified && <span className="text-blue-500 text-xs">✓</span>}
                                </h3>
                                <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                  {getAge(profile.date_of_birth) && <span>{getAge(profile.date_of_birth)}</span>}
                                  {profile.gender && <span>· {profile.gender}</span>}
                                  {profile.health_status && profile.health_status !== "none" && (
                                    <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs flex items-center gap-1">
                                      <Shield className="w-3 h-3" />
                                      {profile.health_status}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                req.type === "dating" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"
                              }`}>
                                {req.type === "dating" ? "Dating" : "Friend"}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm text-gray-600">
                              {profile.country && (
                                <div className="flex items-center gap-2 min-w-0">
                                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span className="truncate">
                                    {profile.city ? `${profile.city}, ` : ""}{profile.country}
                                  </span>
                                </div>
                              )}
                              {profile.education && (
                                <div className="flex items-center gap-2">
                                  <GraduationCap className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span>{profile.education}</span>
                                </div>
                              )}
                              {profile.employment && (
                                <div className="flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span>{profile.employment}</span>
                                </div>
                              )}
                              {profile.kids && (
                                <div className="flex items-center gap-2">
                                  <Baby className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span>{profile.kids === "have" ? "Has kids" : "No kids"}</span>
                                </div>
                              )}
                            </div>

                            {profile.bio && (
                              <p className="text-sm text-gray-700 mb-3 line-clamp-3">
                                {profile.bio}
                              </p>
                            )}

                            {profile.interests && (
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {profile.interests
                                  .split(",")
                                  .map((i) => i.trim())
                                  .filter(Boolean)
                                  .slice(0, 6)
                                  .map((interest, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 bg-gray-50 border border-gray-100 text-gray-600 rounded-full text-xs"
                                    >
                                      {interest}
                                    </span>
                                  ))}
                              </div>
                            )}

                            <div className="flex items-center gap-3 mt-auto pt-3 border-t border-gray-100">
                              {req.phone_number ? (
                                <button
                                  onClick={() => openWhatsApp(req.phone_number)}
                                  className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                                >
                                  <Phone className="w-4 h-4" />
                                  WhatsApp
                                </button>
                              ) : (
                                // P3 FIX: Tap the button when no phone to show dialog
                                <button
                                  onClick={() => showNoPhoneDialog(profile.display_name)}
                                  className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-200 transition-colors"
                                >
                                  <Phone className="w-4 h-4" />
                                  No number
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "incoming" && (
          <div>
            {loadingIncoming ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : incomingRequests.length === 0 ? (
              <div className="text-center py-16">
                <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No incoming requests.</p>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {incomingRequests.map((req) => {
                  const profile = profiles[req.from_user_id];
                  const isLoadingProfile = profilesLoading[req.from_user_id];
                  const isResponding = respondingId === req.request_id;

                  return (
                    <div key={req.request_id} className="bg-white border border-[#E7E5E0] rounded-2xl overflow-hidden shadow-sm">
                      {isLoadingProfile || !profile ? (
                        <div className="p-4 sm:p-6 animate-pulse">
                          <div className="flex gap-4">
                            <div className="w-24 h-24 bg-gray-200 rounded-xl" />
                            <div className="flex-1 space-y-3">
                              <div className="h-4 bg-gray-200 rounded w-3/4" />
                              <div className="h-3 bg-gray-200 rounded w-1/2" />
                              <div className="h-3 bg-gray-200 rounded w-full" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col md:flex-row">
                          <div className="relative w-full md:w-2/5 h-72 sm:h-80 md:h-auto bg-gray-200">
                            {(() => {
                              const images = getGalleryImages(profile);
                              const currentIdx = galleryIndexes[req.from_user_id] || 0;
                              if (images.length === 0) {
                                return (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <Users className="w-16 h-16 text-gray-400" />
                                  </div>
                                );
                              }
                              return (
                                <>
                                  <img
                                    src={images[currentIdx]}
                                    alt={profile.display_name}
                                    className="absolute inset-0 w-full h-full object-cover"
                                  />
                                  {images.length > 1 && (
                                    <>
                                      <button
                                        onClick={(e) => handlePrevImage(e, req.from_user_id)}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
                                      >
                                        <ChevronLeft className="w-5 h-5" />
                                      </button>
                                      <button
                                        onClick={(e) => handleNextImage(e, req.from_user_id)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 rounded-full flex items-center justify-center text-white"
                                      >
                                        <ChevronRight className="w-5 h-5" />
                                      </button>
                                      <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1">
                                        {images.map((_, i) => (
                                          <span
                                            key={i}
                                            className={`h-1.5 rounded-full ${
                                              i === currentIdx ? "w-4 bg-white" : "w-1.5 bg-white/50"
                                            }`}
                                          />
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </div>

                          <div className="flex-1 p-4 sm:p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="font-display text-xl font-semibold flex items-center gap-2">
                                  {profile.display_name || "Anonymous"}
                                  {profile.verified && <span className="text-blue-500 text-xs">✓</span>}
                                </h3>
                                <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                  {getAge(profile.date_of_birth) && <span>{getAge(profile.date_of_birth)}</span>}
                                  {profile.gender && <span>· {profile.gender}</span>}
                                  {profile.health_status && profile.health_status !== "none" && (
                                    <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs flex items-center gap-1">
                                      <Shield className="w-3 h-3" />
                                      {profile.health_status}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                req.type === "dating" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"
                              }`}>
                                {req.type === "dating" ? "Dating" : "Friend"}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm text-gray-600">
                              {profile.country && (
                                <div className="flex items-center gap-2 min-w-0">
                                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span className="truncate">
                                    {profile.city ? `${profile.city}, ` : ""}{profile.country}
                                  </span>
                                </div>
                              )}
                              {profile.education && (
                                <div className="flex items-center gap-2">
                                  <GraduationCap className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span>{profile.education}</span>
                                </div>
                              )}
                              {profile.employment && (
                                <div className="flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span>{profile.employment}</span>
                                </div>
                              )}
                              {profile.kids && (
                                <div className="flex items-center gap-2">
                                  <Baby className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span>{profile.kids === "have" ? "Has kids" : "No kids"}</span>
                                </div>
                              )}
                            </div>

                            {profile.bio && (
                              <p className="text-sm text-gray-700 mb-3 line-clamp-3">
                                {profile.bio}
                              </p>
                            )}

                            {profile.interests && (
                              <div className="flex flex-wrap gap-1.5 mb-4">
                                {profile.interests
                                  .split(",")
                                  .map((i) => i.trim())
                                  .filter(Boolean)
                                  .slice(0, 6)
                                  .map((interest, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 bg-gray-50 border border-gray-100 text-gray-600 rounded-full text-xs"
                                    >
                                      {interest}
                                    </span>
                                  ))}
                              </div>
                            )}

                            <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3 mt-auto pt-3 border-t border-gray-100">
                              <button
                                onClick={() => handleRespond(req.request_id, "accept")}
                                disabled={isResponding}
                                className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50 transition-colors"
                              >
                                {isResponding ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                                Accept
                              </button>
                              <button
                                onClick={() => handleRespond(req.request_id, "reject")}
                                disabled={isResponding}
                                className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
                              >
                                <X className="w-4 h-4" />
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}